import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';

/**
 * Initialize Sentry for frontend error tracking
 * 
 * This function should be called once in the main entry point of the application
 * Set VITE_SENTRY_DSN in your .env file to enable Sentry reporting
 */
export const initializeSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.MODE || 'development';
  
  if (!dsn) {
    console.warn('Sentry DSN not found in environment variables. Sentry will not be initialized.');
    return;
  }
  
  try {
    Sentry.init({
      dsn,
      integrations: [new BrowserTracing()],
      environment,
      
      // Performance monitoring sample rate (adjust based on traffic)
      tracesSampleRate: environment === 'production' ? 0.2 : 1.0,
      
      // Set a uniform sample rate for errors
      sampleRate: environment === 'production' ? 0.5 : 1.0,
      
      // Only send errors in production
      enabled: environment === 'production',
      
      // Enable automatic breadcrumbs for click events, etc.
      autoSessionTracking: true,
      
      // We recommend adjusting this value based on your application
      maxBreadcrumbs: 50,
      
      beforeSend(event) {
        // Don't send errors in development
        if (environment !== 'production') {
          return null;
        }
        
        // Filter out errors that might contain sensitive information
        if (event.request && event.request.url) {
          // Filter out credentials from URLs
          event.request.url = event.request.url.replace(/\/\/[^:]+:[^@]+@/, '//');
        }
        
        return event;
      }
    });
    
    console.log(`Sentry initialized in ${environment} environment`);
  } catch (error) {
    console.error('Failed to initialize Sentry:', error);
  }
};

/**
 * Manually capture an exception with Sentry
 * 
 * @param {Error} error - The error to capture
 * @param {Object} context - Additional context information
 */
export const captureException = (error, context = {}) => {
  Sentry.withScope((scope) => {
    if (context.user) {
      scope.setUser(context.user);
    }
    if (context.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }
    if (context.extras) {
      Object.entries(context.extras).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }
    if (context.level) {
      scope.setLevel(context.level);
    }
    
    Sentry.captureException(error);
  });
};

/**
 * Create an error boundary for React components
 * 
 * @param {Object} options - Options for the error boundary
 * @returns {Function} - Error boundary component
 */
export const createErrorBoundary = (options = {}) => {
  return Sentry.withErrorBoundary(options.component, {
    fallback: options.fallback || function() {
      return document.createElement('div').textContent = 'An error has occurred';
    },
    ...options
  });
};

export default Sentry; 